import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import innatesMiddlewareService from '../services/innatesMiddlewareService';
import { clearMyGapNotesCache } from '../utils/mygapNotesStorage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start using Supabase
    checkUser();
    
    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔐 Auth state changed:', event);
      // myGAP notes cache holds other users' notes: never leave it for the next user.
      if (event === 'SIGNED_OUT') clearMyGapNotesCache();

      if (session?.user) {
        setIsAuthenticated(true);
        setUser(session.user);
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
      setLoading(false);
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const checkAdminStatus = async (email) => {
    try {
      console.log('🔍 Checking admin status for:', email);
      
      const { data: userData, error } = await supabase
        .from('user')
        .select('is_admin')
        .eq('email', email)
        .maybeSingle();
      
      if (error) {
        console.error('❌ Error checking admin status:', error);
        setIsAdmin(false);
        return;
      }
      
      const adminStatus = userData?.is_admin || false;
      setIsAdmin(adminStatus);
      
      if (adminStatus) {
        console.log('👑 User is ADMIN');
      } else {
        console.log('👤 User is regular user');
      }
    } catch (error) {
      console.error('❌ Error in checkAdminStatus:', error);
      setIsAdmin(false);
    }
  };

  const checkUser = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) throw error;
      
      if (session?.user) {
        console.log('✅ User session found:', session.user.email);
        setIsAuthenticated(true);
        setUser(session.user);
        
        // Check if user is admin
        await checkAdminStatus(session.user.email);
      } else {
        console.log('ℹ️ No active session');
        setIsAuthenticated(false);
        setUser(null);
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('❌ Error checking user session:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('🔐 Attempting login with Supabase Auth...');
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      
      console.log('✅ Login successful:', data.user.email);
      setIsAuthenticated(true);
      setUser(data.user);
      
      // Check admin status after login
      await checkAdminStatus(data.user.email);
      
      return { success: true, user: data.user };
    } catch (error) {
      console.error('❌ Login error:', error.message);
      return { success: false, error: error.message };
    }
  };

  const signup = async (email, password, userData) => {
    try {
      console.log('🔐 Attempting signup with Supabase Auth...');
      
      // 1. Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password
      });
      
      if (authError) throw authError;
      
      console.log('✅ Auth user created:', authData.user.email);
      
      // 2. Create user record in custom user table
      const { data: newUser, error: userError } = await supabase
        .from('user')
        .insert({
          id_auth: authData.user.id,
          email: email,
          name_first: userData.firstName,
          name_last: userData.lastName,
          mobile: userData.phone,
          identification_no: userData.icNumber || null,
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (userError) {
        console.error('❌ Error creating user record:', userError);
        // Still continue - auth user was created
      } else {
        console.log('✅ User record created:', newUser);
        
        // 3. Register user with Innates middleware (non-blocking)
        try {
          const middlewareResponse = await innatesMiddlewareService.registerFarmer({
            farmerId: String(newUser.id),
            name: `${userData.firstName} ${userData.lastName}`,
            phone: userData.phone || '',
            icNo: userData.icNumber || newUser.identification_no || ''
          });
          console.log('✅ User registered with Innates middleware:', middlewareResponse);
          
          // Store middleware_id for future sync operations
          if (middlewareResponse?.id) {
            await supabase
              .from('user')
              .update({ middleware_id: middlewareResponse.id })
              .eq('id', newUser.id);
            console.log('✅ Middleware ID stored:', middlewareResponse.id);
          }
        } catch (middlewareError) {
          // Log but don't block registration if middleware fails
          console.warn('⚠️ Innates middleware registration failed (non-blocking):', middlewareError.message);
        }
      }
      
      setIsAuthenticated(true);
      setUser(authData.user);
      // Newly-signed-up users are never admin. Explicitly resetting prevents a
      // previous admin login's `isAdmin = true` from leaking into the fresh
      // user's first dashboard render (which would otherwise show platform-wide
      // org stats + the Admin sidebar group until the next reload).
      setIsAdmin(false);

      return { success: true, user: authData.user, customUser: newUser };
    } catch (error) {
      console.error('❌ Signup error:', error.message);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      console.log('🔐 Logging out...');
      const { error } = await supabase.auth.signOut();

      if (error) throw error;
      clearMyGapNotesCache();

      console.log('✅ Logout successful');
      setIsAuthenticated(false);
      setUser(null);
      // Reset isAdmin so the next user that signs in / signs up on this
      // tab doesn't inherit a stale admin role from the previous session.
      setIsAdmin(false);

      return { success: true };
    } catch (error) {
      console.error('❌ Logout error:', error.message);
      return { success: false, error: error.message };
    }
  };

  const value = {
    isAuthenticated,
    user,
    isAdmin,
    loading,
    login,
    signup,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
